import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandshake, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDeals } from '@/api/useDeals';
import { KanbanBoard } from '@/components/Deals/KanbanBoard';
import { DealListView } from '@/components/Deals/DealListView';
import { DealCalendarView } from '@/components/Deals/DealCalendarView';
import { Button } from '@/components/ui';
import { useModalStore } from '@/stores';
import { QuickCreateDealModal } from '@/components/Deals/QuickCreateDealModal';
import type { DealViewMode } from '@/types/deal';
import styles from './Deals.module.css';

export const Deals = () => {
  const [viewMode, setViewMode] = useState<DealViewMode>('kanban');
  const { data: deals = [], isLoading } = useDeals();
  const { setHeaderSlot } = useOutletContext<{ setHeaderSlot: (node: ReactNode) => void }>();
  const { openModal } = useModalStore();

  const openCreate = useCallback(() => openModal(<QuickCreateDealModal />), [openModal]);

  useEffect(() => {
    setHeaderSlot(
      <div className={styles.headerControls}>
        <div className={styles.viewToggle}>
          {(['kanban', 'list', 'calendar'] as DealViewMode[]).map((mode) => (
            <button
              key={mode}
              className={`${styles.viewButton} ${viewMode === mode ? styles.viewButtonActive : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {mode === 'kanban' ? 'Kanban' : mode === 'list' ? 'List' : 'Calendar'}
            </button>
          ))}
        </div>
        <Button variant="primary" size="sm" onClick={openCreate}>
          + New Deal
        </Button>
      </div>,
    );
    return () => setHeaderSlot(null);
  }, [viewMode, setHeaderSlot, openCreate]);

  if (isLoading) return <div className={styles.loading}>Loading...</div>;

  if (deals.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyCard} onClick={openCreate}>
          <div className={styles.emptyIcon}>
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <h2 className={styles.emptyTitle}>Create your first deal</h2>
          <p className={styles.emptyDescription}>
            Track your business opportunities, manage client relationships, and generate quotations
            with AI.
          </p>
          <Button variant="primary" size="lg">
            <FontAwesomeIcon icon={faPlus} /> New Deal
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {viewMode === 'kanban' && <KanbanBoard deals={deals} />}
      {viewMode === 'list' && <DealListView deals={deals} />}
      {viewMode === 'calendar' && <DealCalendarView deals={deals} />}
    </div>
  );
};
